/**
 * `call.show` in the main pane: what Viko puts on screen during a call, full size (a decision and
 * its option boards, an artifact's project gallery, a build's lane, a workstream's diff, a run,
 * or the passkey batch). The dock stays beside it; Esc or × returns to the page.
 */
import { Link } from '@tanstack/react-router'
import { GitMerge, KeyRound, Rocket, X } from 'lucide-react'
import { useEffect } from 'react'
import { useCall } from '../../lib/call/CallProvider.js'
import { projectHref, runHref } from '../../lib/ia.js'
import { DecisionPanel } from '../decision/DecisionPanel.js'

export function CallShowPane() {
  const c = useCall()
  const t = c.state.show
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') c.closeShow()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })
  if (!t) return null
  const project = (id: string) =>
    c.state.brief?.projects.find((p) => p.id === id)?.name ?? 'Project'
  let title = 'On screen'
  let body: React.ReactNode = null
  switch (t.type) {
    case 'decision':
    case 'artboards': {
      const d = c.state.brief?.projects
        .flatMap((p) => p.decisions)
        .find((x) => x.id === t.decisionId)
      title = (t.type === 'artboards' ? t.title : undefined) ?? d?.question ?? 'Decision'
      body = <DecisionPanel inspectKey={`decision:${t.decisionId}`} showTitle={false} />
      break
    }
    case 'secure-batch':
      title = 'Actions that need your passkey'
      body = <SecureBatchList />
      break
    case 'build':
      title = `${project(t.projectId)} · ${t.lane}`
      body = (
        <p className="call-meta">
          Builds and what's live for this project are on its{' '}
          <Link to={projectHref(t.projectId, 'releases')}>Builds view</Link>.
        </p>
      )
      break
    case 'diff':
      title = 'Changes'
      body = (
        <p className="call-meta">
          The workstream's diff is in its project lane (open the lane's changes).
        </p>
      )
      break
    case 'run':
      title = 'Run'
      body = (
        <p className="call-meta">
          <Link to={runHref(t.runId)}>Open the run</Link>
        </p>
      )
      break
    case 'artifact':
      title = 'Artifact'
      body = <p className="call-meta">The artifact is in its project's gallery (Artifacts view).</p>
      break
  }
  return (
    <section className="call-show" aria-label="Shown by Viko on the call" data-testid="call-show">
      <div className="call-show__h">
        <span>
          <span className="call-proj">Shown by Viko on the call</span>
          <h2>{title}</h2>
        </span>
        <button
          type="button"
          className="call-ctl"
          aria-label="Back to the page"
          onClick={() => c.closeShow()}
        >
          <X size={18} aria-hidden="true" />
        </button>
      </div>
      <div className="call-show__b">{body}</div>
    </section>
  )
}

export function SecureBatchList() {
  const c = useCall()
  const items = c.state.secure.filter((x) => x.state !== 'removed')
  const queued = items.filter((x) => x.state === 'queued')
  if (!items.length) return <p className="call-meta">Nothing is waiting for your passkey.</p>
  return (
    <div className="call-batch">
      <p className="call-meta">
        Queued during this call. Voice can't approve these: anyone near an open mic gets voice-tier
        authority only. One passkey covers all of them, and each appears in History as "via call".
      </p>
      <div className="call-batch__list">
        {items.map((x) => (
          <div key={x.id} className={`call-batch__row is-${x.state}`} data-testid="secure-item">
            {x.kind === 'land.deploy' ? (
              <Rocket size={18} aria-hidden="true" />
            ) : x.kind === 'land.merge' ? (
              <GitMerge size={18} aria-hidden="true" />
            ) : (
              <KeyRound size={18} aria-hidden="true" />
            )}
            <span>
              <span className="call-batch__t">{x.title}</span>
              <br />
              <span className="call-meta">
                {x.production ? 'Production · offered now' : 'Waits for the end of the call'}
                {x.detail ? ` · ${x.detail}` : ''}
                {x.state !== 'queued' ? ` · ${x.state}` : ''}
              </span>
            </span>
            {x.state === 'queued' ? (
              <button type="button" className="call-btn-sec" onClick={() => c.removeSecure(x.id)}>
                <X size={14} aria-hidden="true" />
                Remove
              </button>
            ) : (
              <span />
            )}
          </div>
        ))}
      </div>
      {queued.length ? (
        <button type="button" className="call-btn-pri" onClick={() => void c.approveSecure()}>
          <KeyRound size={16} aria-hidden="true" />
          Approve {queued.length} with passkey
        </button>
      ) : null}
    </div>
  )
}
